import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';
import TimelineView from '../components/TimelineView';
import ProgressChart from '../components/ProgressChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyLearningJourney = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (user) {
      fetchJourney();
    }
  }, [user]);

  const fetchJourney = async () => {
    try {
      const response = await axios.get(`${API}/learning-journey/`, { withCredentials: true });
      setJourney(response.data);
    } catch (error) {
      console.error('Error fetching journey:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (rate) => {
    if (rate >= 0.8) return 'text-green-600 bg-green-100';
    if (rate >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Öğrenme Yolculuğum' : 'My Learning Journey'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-2">
            🎓 {language === 'tr' ? 'Öğrenme Yolculuğum' : 'My Learning Journey'}
          </h1>
          <p className="text-blue-100 text-lg">
            {language === 'tr' 
              ? 'İlerlemenizi takip edin ve hedeflerinize ulaşın'
              : 'Track your progress and achieve your goals'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-blue-600">
              {journey?.total_study_hours?.toFixed(1) || 0}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Toplam Saat' : 'Total Hours'}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-green-600">
              {((journey?.overall_accuracy || 0) * 100).toFixed(0)}%
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Genel Başarı' : 'Overall Accuracy'}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-purple-600">
              {journey?.total_questions_attempted || 0}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Çözülen Soru' : 'Questions Solved'}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-orange-600">
              {journey?.assigned_tasks?.filter(t => t.completed).length || 0}
              <span className="text-lg text-gray-500">
                /{journey?.assigned_tasks?.length || 0}
              </span>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Görev Tamamlandı' : 'Tasks Completed'}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="flex border-b overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'overview'
                  ? 'border-b-4 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {language === 'tr' ? '📊 Genel Durum' : '📊 Overview'}
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'analytics'
                  ? 'border-b-4 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {language === 'tr' ? '📈 Analiz' : '📈 Analytics'}
            </button>
            <button
              onClick={() => setActiveTab('topics')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'topics'
                  ? 'border-b-4 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {language === 'tr' ? '📚 Konular' : '📚 Topics'}
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'tasks'
                  ? 'border-b-4 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {language === 'tr' ? '✅ Görevler' : '✅ Tasks'}
            </button>
            <button
              onClick={() => setActiveTab('badges')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'badges'
                  ? 'border-b-4 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {language === 'tr' ? '🏆 Rozetler' : '🏆 Badges'}
              {journey?.new_badges_count > 0 && (
                <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                  {journey.new_badges_count}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('mistakes')}
              className={`px-6 py-4 font-semibold transition ${
                activeTab === 'mistakes'
                  ? 'border-b-4 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {language === 'tr' ? '❌ Hatalarım' : '❌ Mistakes'}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">
                {language === 'tr' ? '📊 Genel Durum' : '📊 Overview'}
              </h2>
              
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="font-bold text-lg mb-3">
                  {language === 'tr' ? 'Güçlü Yönlerim' : 'My Strengths'}
                </h3>
                {journey?.topics?.filter(t => t.correct_rate >= 0.8).length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {journey.topics
                      .filter(t => t.correct_rate >= 0.8)
                      .map((topic, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold"
                        >
                          {topic.topic} ({(topic.correct_rate * 100).toFixed(0)}%)
                        </span>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-600">{language === 'tr' ? 'Henüz veri yok' : 'No data yet'}</p>
                )}
              </div>

              <div className="bg-red-50 p-6 rounded-xl">
                <h3 className="font-bold text-lg mb-3">
                  {language === 'tr' ? 'Geliştirilmesi Gerekenler' : 'Areas to Improve'}
                </h3>
                {journey?.topics?.filter(t => t.correct_rate < 0.6).length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {journey.topics
                      .filter(t => t.correct_rate < 0.6)
                      .map((topic, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-semibold"
                        >
                          {topic.topic} ({(topic.correct_rate * 100).toFixed(0)}%)
                        </span>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-600">{language === 'tr' ? 'Harika gidiyorsun! 🎉' : 'Great job! 🎉'}</p>
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">
                {language === 'tr' ? '📈 Detaylı Analiz' : '📈 Detailed Analytics'}
              </h2>
              
              {/* Progress Chart */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
                <h3 className="font-bold text-lg mb-4">
                  {language === 'tr' ? 'İlerleme Grafiği' : 'Progress Chart'}
                </h3>
                <ProgressChart 
                  data={journey?.progress_data || []}
                  language={language}
                />
              </div>

              {/* Study Patterns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4">
                    {language === 'tr' ? '📅 Çalışma Düzeni' : '📅 Study Patterns'}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'tr' ? 'En Aktif Gün' : 'Most Active Day'}
                      </span>
                      <span className="font-semibold">
                        {journey?.most_active_day || (language === 'tr' ? 'Veri yok' : 'No data')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'tr' ? 'Ortalama Seans' : 'Avg Session'}
                      </span>
                      <span className="font-semibold">
                        {journey?.avg_session_duration || 0} min
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'tr' ? 'Çalışma Serisi' : 'Study Streak'}
                      </span>
                      <span className="font-semibold">
                        {journey?.study_streak || 0} {language === 'tr' ? 'gün' : 'days'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-xl p-6">
                  <h3 className="font-bold text-lg mb-4">
                    {language === 'tr' ? '🎯 Hedef Durumu' : '🎯 Goal Status'}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        {language === 'tr' ? 'Haftalık Hedef' : 'Weekly Goal'}
                      </span>
                      <span className="font-semibold">
                        {journey?.weekly_goal_progress || 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-green-400 to-blue-500"
                        style={{ width: `${journey?.weekly_goal_progress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Trends */}
              <div className="bg-white border rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4">
                  {language === 'tr' ? '📊 Performans Trendleri' : '📊 Performance Trends'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {journey?.improvement_rate ? `+${(journey.improvement_rate * 100).toFixed(1)}%` : '0%'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {language === 'tr' ? 'Bu Hafta İyileşme' : 'This Week Improvement'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {journey?.consistency_score ? `${(journey.consistency_score * 100).toFixed(0)}%` : '0%'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {language === 'tr' ? 'Tutarlılık Skoru' : 'Consistency Score'}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {journey?.focus_areas?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">
                      {language === 'tr' ? 'Odak Alanları' : 'Focus Areas'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Topics Tab */}
          {activeTab === 'topics' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                {language === 'tr' ? '📚 Konu Başarı Oranları' : '📚 Topic Performance'}
              </h2>
              
              {journey?.topics && journey.topics.length > 0 ? (
                <div className="space-y-3">
                  {journey.topics
                    .sort((a, b) => b.correct_rate - a.correct_rate)
                    .map((topic, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-800">{topic.topic}</span>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${getPerformanceColor(topic.correct_rate)}`}>
                            {(topic.correct_rate * 100).toFixed(0)}%
                          </span>
                        </div>
                        
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div
                            className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                            style={{ width: `${topic.correct_rate * 100}%` }}
                          ></div>
                        </div>
                        
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>{topic.attempts} {language === 'tr' ? 'deneme' : 'attempts'}</span>
                          <span>{topic.trend && `📈 ${topic.trend}`}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-600 text-center py-8">
                  {language === 'tr' ? 'Henüz konu verisi yok' : 'No topic data yet'}
                </p>
              )}
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                {language === 'tr' ? '✅ Görevlerim' : '✅ My Tasks'}
              </h2>
              
              {/* Timeline View */}
              <div className="mb-8">
                <TimelineView
                  tasks={journey?.assigned_tasks || []}
                  language={language}
                />
              </div>
              
              {journey?.assigned_tasks && journey.assigned_tasks.length > 0 ? (
                <div className="space-y-3">
                  {journey.assigned_tasks.map((task, idx) => (
                    <div
                      key={idx}
                      className={`border rounded-lg p-4 ${
                        task.completed ? 'bg-green-50 border-green-200' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-2xl">
                              {task.completed ? '✅' : '⏳'}
                            </span>
                            <h3 className="font-semibold text-gray-800">{task.title || task.topic}</h3>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {task.task_type || task.type}
                            </span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              {task.difficulty}
                            </span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                              {task.estimated_minutes || task.estimatedMinutes} min
                            </span>
                          </div>
                          
                          {task.description && (
                            <p className="text-sm text-gray-600">{task.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-center py-8">
                  {language === 'tr' ? 'Henüz görev yok. Çalışma planı oluşturun!' : 'No tasks yet. Create a study plan!'}
                </p>
              )}
            </div>
          )}

          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold mb-4">
                {language === 'tr' ? '🏆 Rozetlerim' : '🏆 My Badges'}
              </h2>
              
              {/* New Badges Alert */}
              {journey?.new_badges_count > 0 && (
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white p-6 rounded-xl mb-6">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">🎉</span>
                    <div>
                      <h3 className="font-bold text-lg">
                        {language === 'tr' ? 'Tebrikler!' : 'Congratulations!'}
                      </h3>
                      <p>
                        {language === 'tr' 
                          ? `${journey.new_badges_count} yeni rozet kazandınız!`
                          : `You earned ${journey.new_badges_count} new badge${journey.new_badges_count > 1 ? 's' : ''}!`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Badges Grid */}
              {journey?.badges && journey.badges.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {journey.badges.map((badge, idx) => (
                    <div
                      key={idx}
                      className={`relative p-6 rounded-xl border-2 transition-all hover:shadow-lg ${
                        badge.earned
                          ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300'
                          : 'bg-gray-50 border-gray-200 opacity-60'
                      }`}
                    >
                      {badge.is_new && (
                        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {language === 'tr' ? 'YENİ' : 'NEW'}
                        </div>
                      )}
                      
                      <div className="text-center">
                        <div className="text-4xl mb-3">
                          {badge.earned ? badge.icon : '🔒'}
                        </div>
                        <h3 className="font-bold text-lg mb-2">
                          {badge.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {badge.description}
                        </p>
                        
                        {badge.earned ? (
                          <div className="space-y-2">
                            <div className="text-xs text-green-600 font-semibold">
                              {language === 'tr' ? '✅ Kazanıldı' : '✅ Earned'}
                            </div>
                            {badge.earned_date && (
                              <div className="text-xs text-gray-500">
                                {new Date(badge.earned_date).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="text-xs text-gray-500">
                              {language === 'tr' ? 'Henüz kazanılmadı' : 'Not earned yet'}
                            </div>
                            {badge.progress && (
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-500"
                                  style={{ width: `${badge.progress}%` }}
                                ></div>
                              </div>
                            )}
                            {badge.requirement && (
                              <div className="text-xs text-gray-600">
                                {badge.requirement}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🏆</div>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    {language === 'tr' ? 'Henüz rozet yok' : 'No badges yet'}
                  </h3>
                  <p className="text-gray-500">
                    {language === 'tr' 
                      ? 'Çalışmaya devam edin ve ilk rozetinizi kazanın!'
                      : 'Keep studying to earn your first badge!'
                    }
                  </p>
                </div>
              )}

              {/* Badge Categories */}
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="font-bold text-lg mb-4">
                  {language === 'tr' ? '🎯 Rozet Kategorileri' : '🎯 Badge Categories'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-blue-800">
                      {language === 'tr' ? '📚 Öğrenme Rozetleri' : '📚 Learning Badges'}
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• {language === 'tr' ? 'İlk Adım - İlk soruyu çöz' : 'First Step - Solve first question'}</li>
                      <li>• {language === 'tr' ? 'Hızlı Öğrenci - 10 soru çöz' : 'Quick Learner - Solve 10 questions'}</li>
                      <li>• {language === 'tr' ? 'Bilgin - 100 soru çöz' : 'Scholar - Solve 100 questions'}</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-green-800">
                      {language === 'tr' ? '🎯 Başarı Rozetleri' : '🎯 Achievement Badges'}
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• {language === 'tr' ? 'Mükemmel - %90 başarı oranı' : 'Perfect - 90% accuracy rate'}</li>
                      <li>• {language === 'tr' ? 'Tutarlı - 7 gün üst üste çalış' : 'Consistent - Study 7 days in a row'}</li>
                      <li>• {language === 'tr' ? 'Uzman - Bir konuda uzmanlaş' : 'Expert - Master a topic'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mistakes Tab */}
          {activeTab === 'mistakes' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                {language === 'tr' ? '🔍 Son Hatalarım' : '🔍 Recent Mistakes'}
              </h2>
              
              {journey?.recent_mistakes && journey.recent_mistakes.length > 0 ? (
                <div className="space-y-3">
                  {journey.recent_mistakes.map((mistake, idx) => (
                    <div key={idx} className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                      <p className="text-gray-800">{mistake}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-center py-8">
                  {language === 'tr' ? 'Henüz hata kaydı yok' : 'No mistakes recorded yet'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyLearningJourney;
